'use client';

import type { CSSProperties } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { mergeProps } from 'react-aria';
import type { TextProps, TextVariants } from 'components/Text';
import { textStyles } from 'components/Text';
import { TextWithTooltip } from 'components/Text/TextWithTooltip';
import { useControlledComponent } from 'hooks/useControlledComponent';
import { useEmulateButton } from 'hooks/useEmulateButton';
import { usePrevious } from 'hooks/usePrevious';
import styled, { css } from 'styled-components';
import { getBackgroundColor } from 'theme-utils/utils/themeColorSelectors';
import { whenFocused, whenHovered } from 'utils/css-selectors';
import { isComposingEvent } from 'utils/is-composing';
import { noop } from 'utils/noop';
import { getInteractionModality } from '@react-aria/interactions';
import { focusWithoutScrolling } from '@react-aria/utils';
import type {
  ThemeColorsBackground,
  ThemeColorsText,
  ThemeSpaces,
} from '@frameio/vapor-theme';
import { theme } from '@frameio/vapor-theme';
import type { InlineInputProps } from './InlineEditInput';
import { InlineInput } from './InlineEditInput';
import type { AutoSelectOption } from './types';

type InlineEditColor = ThemeColorsText | CSSProperties['color'];

export type InlineEditProps = {
  /** The maximum number of lines shown when not editing. */
  lineClamp?: number;
  /**
   * Whether editing functionality should be enabled. This might
   * be useful if you want to disable editing until a parent element
   * has been activated.
   */
  enabled?: boolean;
  /**
   * Maximum number of rows to display when editing. This should either
   * be equal to or greater than lineClamp. If not included, editing
   * max row count will be equal to lineClamp (if included) or unlimited.
   */
  maxRowsWhileEditing?: number;
  /** Max characters to enable when editing */
  maxLength?: number;
  /** The initial text value. */
  defaultValue?: string;
  /** The initial text value, when used as a controlled component. */
  value?: string;
  /** A callback for canceling inline edit when used as a controlled component. */
  onEditingCancelled?: () => void;
  /** A callback for handling controlled value changes. */
  onValueChange?: (value: string) => void;
  /** Called when the input value has changed upon enter or blur */
  onSubmit?: (value: string) => void;
  /**
   * The delay in ms before editing mode is visible after the target element
   * has been clicked.
   */
  delayToEditVisible?: number;
  /**
   * Optionally controlled editing prop. By default, the component will
   * handle this internally.
   */
  editing?: boolean;
  /**
   * Optional callback to be called when the editing state changes.
   */
  onEditingChange?: (editing: boolean) => void;
  /**
   * When not use as a controlled value, set the default editing state.
   */
  defaultEditing?: boolean;
  /** Called when input blur occurs */
  onBlur?: () => void;
  /**
   * Choose the auto select input behavior when entering edit mode, or provide
   * your own via a function.
   */
  autoSelect?: AutoSelectOption;

  /** The color of the text. */
  color?: InlineEditColor;
  /**
   * Optionally override the line-height. This should be used sparingly,
   * and typically you should instead rely on text variants.
   */
  lineHeight?: number;
  /**
   * Optionally enable newlines. If enabled, new lines can be created by hitting
   * Shift + Enter at the same time. Hitting Enter otherwise will still submit.
   */
  enableNewlines?: boolean;
  /** A label for the input field. */
  'aria-label': string;
  /** Enable a hover background on non editing text. */
  hoverBackgroundColor?: ThemeColorsBackground;
  /** Optional override of the `userSelect` style rule on text when not editing */
  userSelect?: CSSProperties['userSelect'];
  /** An optional placeholder to render. */
  placeholder?: string;
  /** CSS Cursor type to show in non editing mode */
  cursor?: CSSProperties['cursor'];
  /** Optional Test Id */
  'data-testid'?: string;
  /** Optionally enable showing a tooltip when text is truncated */
  enableTooltip?: boolean;
  /** Automatically trim whitespace upon blur. */
  autoTrim?: boolean;
  /**
   * Optional horizontal padding around the input element. Either
   * a number from the theme or a string with a unit.
   */
  paddingX?: ThemeSpaces | string;
  /**
   * Optional vertical padding around the input element. Either
   * a number from the theme or a string with a unit.
   */
  paddingY?: ThemeSpaces | string;
} & Pick<TextProps, 'variant' | 'fontWeight'> &
  Pick<InlineInputProps, 'focusedBackgroundColor'>;

/**
 * @deprecated use TextWithInlineEditing instead.
 */
export function InlineEdit({
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  autoSelect = 'all',
  autoTrim = false,
  color = 'body',
  cursor,
  defaultEditing,
  defaultValue,
  delayToEditVisible = 350,
  editing: editingProp,
  enableNewlines = false,
  enableTooltip = false,
  enabled = true,
  focusedBackgroundColor = 'primary',
  fontWeight,
  hoverBackgroundColor,
  lineClamp,
  lineHeight,
  maxLength,
  maxRowsWhileEditing,
  onEditingCancelled,
  onEditingChange,
  onSubmit = noop,
  onValueChange,
  paddingX: paddingXProp = '0px',
  paddingY: paddingYProp = '0px',
  placeholder,
  userSelect = 'none',
  value: valueProp,
  variant = 'primary',
  ...other
}: InlineEditProps) {
  const cloneRef = useRef(null);
  const ref = useRef(null);
  const inputRef = useRef(null);

  const [value, setValue] = useControlledComponent(
    valueProp,
    onValueChange,
    defaultValue
  );

  const [dirty, setDirty] = useState(false);

  const [editingState, setEditing] = useControlledComponent(
    editingProp,
    onEditingChange,
    defaultEditing
  );

  const timerRef = useRef(null);

  const editing = editingState && enabled;

  function onTextCloneClick(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();

    if (e.button === 2) return;

    if (enabled && !editingState && !timerRef.current) {
      // @ts-expect-error FIXME(strictNullChecks)
      timerRef.current = setTimeout(() => {
        setEditing(true);
        timerRef.current = null;
      }, delayToEditVisible);
    }
  }

  useEffect(() => {
    return () => {
      // @ts-expect-error FIXME(strictNullChecks)
      clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [enabled]);

  function endEditing() {
    let valueToSubmit = value;

    if (autoTrim && value) {
      valueToSubmit = value.trim();
    }

    // Forcibly unblur the input element to prevent focus glitches
    // in Safari
    // @ts-expect-error FIXME(strictNullChecks)
    if (inputRef.current?.blur) {
      // @ts-expect-error FIXME(strictNullChecks)
      inputRef.current.blur();
    }

    setEditing(false);

    /** Ensure that we update our input with the trimmed value */
    // @ts-expect-error FIXME(strictNullChecks)
    setValue(valueToSubmit);

    /**
     * When a placeholder is added, the field can be saved
     * with no values.
     */
    if (placeholder && value !== defaultValue) {
      // @ts-expect-error FIXME(strictNullChecks)
      onSubmit(valueToSubmit);
      return;
    }

    /**
     * Without a placeholder, we require that a user
     * enter and save a value. If they don't on blur,
     * then we reset that value to the initially provided
     * value.
     */
    if (value && value !== defaultValue) {
      // @ts-expect-error FIXME(strictNullChecks)
      onSubmit(valueToSubmit);
    } else if (typeof defaultValue !== 'undefined') {
      setValue(defaultValue);
    }
  }

  const prevEnabled = usePrevious(enabled);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (prevEnabled !== enabled && !enabled && dirty) {
      setDirty(false);
      endEditing();
    }
  });

  const stopPropagation = (e: React.MouseEvent<HTMLTextAreaElement>) =>
    e.stopPropagation();

  const stopPropagationAndPreventDefault = (
    e: React.MouseEvent<HTMLTextAreaElement>
  ) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const mergedProps = mergeProps(
    {
      onBlur: endEditing,
      onClick: stopPropagationAndPreventDefault,
      onDoubleClick: stopPropagation,
      onMouseDown: stopPropagation,
      onMouseMove: stopPropagation,
      onPointerDown: stopPropagation,
      onPointerMove: stopPropagation,
    },
    other
  );

  const wasEditing = useRef(editing);

  useEffect(() => {
    if (
      wasEditing.current &&
      !editing &&
      getInteractionModality() === 'keyboard'
    ) {
      // @ts-expect-error FIXME(strictNullChecks)
      const targetButton = ref.current?.querySelector('[role="button"]');
      if (targetButton) {
        focusWithoutScrolling(targetButton);
      }
    }

    wasEditing.current = editing;
  }, [editing]);

  const { bind } = useEmulateButton(ref, 'button');

  const valueToRenderString: React.ReactNode = `${value || placeholder || ' '}`;

  const interactiveTextProps =
    enabled &&
    !editing &&
    mergeProps(bind, {
      'aria-label': `Edit text: ${valueToRenderString}`,
      onPointerDown: onTextCloneClick,
      onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          setEditing(true);
        }
      },
      onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    });

  function onInputKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    e.stopPropagation();
    if (isComposingEvent(e)) return;

    /**
     * If new lines are not enabled or they are enabled but the Shift key is not
     * held down when Enter is pressed, then prevent default behavior and end
     * editing
     */
    if (e.key === 'Enter') {
      if (!enableNewlines || (enableNewlines && !e.shiftKey)) {
        e.preventDefault();
        // @ts-expect-error FIXME(strictNullChecks)
        inputRef.current.blur();
      }
    }

    // Reset to default value and end editing
    if (e.key === 'Escape') {
      e.preventDefault();
      setEditing(false);

      if (typeof defaultValue !== 'undefined') {
        setValue(defaultValue);
      } else {
        onEditingCancelled?.();
      }
    }
  }

  const renderPlaceholder = placeholder && !value;

  const valueToRender = enableNewlines
    ? (valueToRenderString as string).split('\n').map((line, index) => (
        <React.Fragment key={index}>
          {line}
          <br />
        </React.Fragment>
      ))
    : valueToRenderString;

  // @ts-expect-error FIXME(noImplicitAny)
  const paddingX = theme.spaces[paddingXProp] ?? paddingXProp;
  // @ts-expect-error FIXME(noImplicitAny)
  const paddingY = theme.spaces[paddingYProp] ?? paddingYProp;

  return (
    <StyledGridContainer
      ref={ref}
      $color={renderPlaceholder ? 'tertiary' : color}
      // @ts-expect-error FIXME(strictNullChecks)
      $fontWeight={fontWeight}
      $hoverBackgroundColor={enabled ? hoverBackgroundColor : undefined}
      $lineClamp={lineClamp}
      $lineHeight={lineHeight}
      $paddingX={paddingX}
      $paddingY={paddingY}
      $variant={variant}
      data-testid={dataTestId}
    >
      {editing ? (
        <StyledEditingContainer>
          <TextClonePadding $paddingX={paddingX} $paddingY={paddingY}>
            <TextClone
              // @ts-expect-error FIXME(strictNullChecks)
              $clamp={maxRowsWhileEditing}
              $hidden
              $wordBreak="break-word"
            >
              {valueToRender}
            </TextClone>
          </TextClonePadding>
          <InlineInput
            ref={inputRef}
            autoSelect={autoSelect}
            focusedBackgroundColor={focusedBackgroundColor}
            maxLength={maxLength}
            maxRows={maxRowsWhileEditing}
            padding={`${paddingY} ${paddingX}`}
            {...mergedProps}
            aria-label={ariaLabel}
            onChange={(e) => {
              setDirty(true);
              setValue(e.target.value);
            }}
            onKeyDown={onInputKeyDown}
            placeholder={placeholder}
            value={value}
          />
        </StyledEditingContainer>
      ) : (
        <TextWithTooltip
          shouldRender={enableTooltip}
          title={valueToRenderString}
          // @ts-expect-error FIXME(strictNullChecks)
          truncate={lineClamp}
        >
          <TextClonePaddingWrapper
            {...interactiveTextProps}
            $paddingX={paddingX}
            $paddingY={paddingY}
          >
            <TextClone
              ref={cloneRef}
              // @ts-expect-error FIXME(strictNullChecks)
              $clamp={lineClamp}
              $cursor={cursor}
              $userSelect={editing ? 'none' : userSelect}
              $wordBreak={lineClamp === 1 ? 'break-all' : 'break-word'}
              aria-hidden={editing}
            >
              {valueToRender}
            </TextClone>
          </TextClonePaddingWrapper>
        </TextWithTooltip>
      )}
    </StyledGridContainer>
  );
}

const StyledEditingContainer = styled.div`
  display: inline-grid;
`;

type StyledGridContainerProps = {
  $color: InlineEditColor;
  $variant: TextVariants;
  $lineClamp?: number;
  $lineHeight?: number;
  $hoverBackgroundColor?: ThemeColorsBackground;
  $fontWeight: keyof typeof theme.fontWeights;
  $paddingX: string;
  $paddingY: string;
};

const maxHeight = (lineClamp: number, paddingY?: string) => {
  if (lineClamp && paddingY) {
    return `calc(${lineClamp} * var(--line-height) + ${paddingY} * 2)`;
  }

  return lineClamp ? `calc(${lineClamp} * var(--line-height))` : '';
};

const hoverStates = (p: StyledGridContainerProps) => {
  if (p.$hoverBackgroundColor) {
    return css`
      cursor: text;

      &:before {
        background: transparent;
        content: '';
        inset: -2px;
        position: absolute;
        z-index: 0;
        transition: background 50ms ${theme.transitions.eases.linear};
        border-radius: ${theme.radii[4]};
        pointer-events: none;
      }

      ${whenHovered} {
        &:before {
          background: ${getBackgroundColor(p.$hoverBackgroundColor)};
        }
      }
    `;
  }

  return css``;
};

const StyledGridContainer = styled.div<StyledGridContainerProps>`
  color: ${(p) => (theme.colors.text as any)[p.$color!] || p.$color};
  ${(p) => textStyles[p.$variant]}
  ${(p) =>
    typeof p.$lineHeight === 'number' && `--line-height: ${p.$lineHeight}px`};
  display: inline-grid;
  font-weight: ${(p) => theme.fontWeights[p.$fontWeight]};
  max-height: ${(p) => maxHeight(p.$lineClamp as any, p.$paddingY as any)};
  outline: none;
  position: relative;
  ${hoverStates};
`;

const lineClamp = (p: { $clamp: number }) => {
  return p.$clamp
    ? css`
        -webkit-box-orient: vertical;
        -webkit-line-clamp: ${p.$clamp};
        display: -webkit-box;
        /* Avoid Chrome clipping the final line when line-height equals max-height. */
        line-height: calc(var(--line-height) - 0.01px);
        overflow: hidden;
      `
    : '';
};

type TextClonePaddingProps = {
  $paddingX: string;
  $paddingY: string;
  children?: React.ReactElement<any>;
};
const TextClonePadding = styled.div<TextClonePaddingProps>`
  grid-area: 1 / 1 / 1 /1;
  isolation: isolate;
  outline: none;
  padding: ${(p) => `${p.$paddingY} ${p.$paddingX}`};

  ${whenFocused} {
    &:before {
      border-radius: ${theme.radii[6]};
      box-shadow: 0 0 0 1.5px ${theme.colors.border.interactive.focused};
      content: ' ';
      inset: -2px -4px;
      position: absolute;
    }
  }
`;

const TextClonePaddingWrapper = React.forwardRef(
  ({ children, ...props }: TextClonePaddingProps, ref) => (
    <TextClonePadding {...props}>
      {React.cloneElement(children as any, { ref })}
    </TextClonePadding>
  )
);

const TextClone = styled.span<{
  $clamp: number;
  $hidden?: boolean;
  $cursor?: CSSProperties['cursor'];
  $userSelect?: CSSProperties['userSelect'];
  $wordBreak: 'break-all' | 'break-word';
}>`
  color: inherit;
  cursor: ${(p) => p.$cursor};
  font: inherit;
  letter-spacing: inherit;
  max-height: ${(p) => maxHeight(p.$clamp)};

  padding: 0;
  ${lineClamp};
  resize: none;
  user-select: ${(p) => p.$userSelect};
  visibility: ${(p) => (p.$hidden ? 'hidden' : 'inherit')};
  white-space: pre-wrap;
  width: auto;
  word-break: ${(p) => p.$wordBreak};
  z-index: 1;
`;
